import { useContext } from "react";
import styles from "./TemplateSelectionScreen.module.css";
import { Button, Card, Icon, Tooltip } from "@shopify/polaris";
import { ArrowLeftIcon, InfoIcon } from "@shopify/polaris-icons";
import { Link } from "react-router-dom";
import { StandardBundleContext } from "../../apps/Bundles/Standard/Standard";
import { VolumeBundleContext } from "../../apps/Bundles/VolumeDiscount/VolumeDiscount";
import { FBTBundleContext } from "../../apps/Bundles/FTB/FBT";
import { WIDGET_POSITIONS_OPT } from "../../apps/Bundles/constants";
import {
    BBFooterTemplateInfo,
    BBTemplateInfo,
    BXGYTemplateInfo,
    FBTTemplateInfo,
    StandardTemplateInfo,
    VolumeTemplateInfo,
} from "./TemplateSelectionConstants";
import { BxgyBundleContext } from "../../apps/Bundles/Bxgy/Bxgy";
import { BundleBuilderContext } from "../../apps/Bundles/BundleBuilder/BundleBuilder";
import { PlaceholderContext } from "../../apps/Bundles/Placeholder/Placeholder";
import { PlaceholderTemplateInfo } from "./TemplateSelectionConstants";

const BUNDLE_CONTEXT_MAP: Record<string, { getContext: () => any; templates: any; footerTemplates?: any }> = {
    standard: { getContext: () => StandardBundleContext, templates: StandardTemplateInfo },
    volume: { getContext: () => VolumeBundleContext, templates: VolumeTemplateInfo },
    fbt: { getContext: () => FBTBundleContext, templates: FBTTemplateInfo },
    bxgy: { getContext: () => BxgyBundleContext, templates: BXGYTemplateInfo },
    bundle_builder: {
        getContext: () => BundleBuilderContext,
        templates: BBTemplateInfo,
        footerTemplates: BBFooterTemplateInfo,
    },
    bb_placeholder: {
        getContext: () => PlaceholderContext,
        templates: PlaceholderTemplateInfo,
    },
};

interface TemplateSelectionScreenProps {
    bundleConfig: any;
    dispatchBundleConfig: any;
    makeClean: () => void;
    setConfig?: any;
}

const BUNDLE_FIELD_KEYS: Record<string, { template: string; position?: string; footer_layout?: string }> = {
    standard: {
        template: "selected_template_standard",
    },
    volume: {
        template: "selected_template_volume",
    },
    fbt: {
        template: "selected_template_fbt",
        position: "new_bundle_position",
    },
    product_addons: {
        template: "selected_template_addons",
    },
    bxgy: {
        template: "selected_template_bxgy",
    },
    bundle_builder: {
        template: "selected_template_bb",
        footer_layout: "footer_layout",
    },
    bb_placeholder: {
        template: "selected_template_placeholder",
    },
};

const TemplateSelectionScreen = ({
    bundleConfig,
    dispatchBundleConfig,
    makeClean,
    setConfig,
}: TemplateSelectionScreenProps) => {
    const bundleType = bundleConfig.bundle_type;
    const { getContext, templates, footerTemplates } = BUNDLE_CONTEXT_MAP[bundleType];
    const Context = getContext();
    const bundleContext: any = useContext(Context);

    const fieldKeys = BUNDLE_FIELD_KEYS[bundleType];
    // always grab the template
    const selected_template = bundleContext.fields[fieldKeys.template];
    // only grab position if it exists
    const new_bundle_position = fieldKeys.position ? bundleContext.fields[fieldKeys.position] : undefined;
    // only grab footer_layout if it exists
    const footer_layout = fieldKeys.footer_layout ? bundleContext.fields[fieldKeys.footer_layout] : undefined;

    const handleChangeSelectedTemplate = (template: string) => {
        dispatchBundleConfig({ key: fieldKeys.template, payload: template });
        selected_template.onChange(template);

        if (setConfig) {
            setConfig((ps: any) => ({
                ...ps,
                [fieldKeys.template]: template,
            }));
        }

        // Special FBT case for widget position
        if (bundleType === "fbt") {
            const resolvedPosition =
                template === "vertical" ? WIDGET_POSITIONS_OPT.BELOW_CART : WIDGET_POSITIONS_OPT.FBT_DEFAULT;
            dispatchBundleConfig({ key: fieldKeys.position, payload: resolvedPosition });
            new_bundle_position.onChange(resolvedPosition);
        }

        makeClean();
    };

    const handleChangeSelectedFooterLayout = (template: string) => {
        dispatchBundleConfig({ key: "footer_layout", payload: template });
        footer_layout.onChange(template);

        makeClean();
    };

    return (
        <div className={styles.templateSelectionWrapper}>
            <h1 className={styles.templateSelectionHeading}>
                <Link to={"/"}>
                    <ArrowLeftIcon width={18} className={styles.templateSelectionBackButton} />
                </Link>
                Choose a template
            </h1>

            {bundleConfig?.bundle_type == "bundle_builder" && footerTemplates.length > 0 && (
                <Card>
                    <div className={styles.templateSelectionSubHeading}>Choose footer layout</div>
                    <div
                        className={`${styles.templateSelectionGrid} ${
                            templates.length === 2 ? styles.two : styles.three
                        }`}
                    >
                        {footerTemplates.map((_t: any) => (
                            <div
                                key={_t.template}
                                className={styles.templateSelectionCard}
                                onClick={() => handleChangeSelectedFooterLayout(_t.template)}
                            >
                                {_t.icon}

                                <div className={styles.templateSelectionCardBottomRow}>
                                    <div className={styles.templateSelectionCardLabelRow}>
                                        {_t.label}
                                        {_t.hoverText && (
                                            <Tooltip content={_t.hoverText}>
                                                <Icon source={InfoIcon} tone="base" />
                                            </Tooltip>
                                        )}
                                    </div>
                                    <Button
                                        variant="secondary"
                                        onClick={
                                            ((e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                handleChangeSelectedFooterLayout(_t.template);
                                            }) as unknown as () => void
                                        }
                                    >
                                        {bundleConfig?.footer_layout == _t.template ? "Selected" : "Select"}
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            <Card>
                <div className={styles.templateSelectionSubHeading}>Select template from below</div>
                <div
                    className={`${styles.templateSelectionGrid} ${templates.length === 2 ? styles.two : styles.three}`}
                >
                    {templates.map((_t: any) => (
                        <div
                            key={_t.template}
                            className={styles.templateSelectionCard}
                            onClick={() => handleChangeSelectedTemplate(_t.template)}
                        >
                            {_t.icon}

                            <div className={styles.templateSelectionCardBottomRow}>
                                <div className={styles.templateSelectionCardLabelRow}>
                                    <div>
                                        {_t.label}
                                        {_t.hoverText && (
                                            <Tooltip content={_t.hoverText}>
                                                <Icon source={InfoIcon} tone="base" />
                                            </Tooltip>
                                        )}
                                        {_t.desc && (
                                            <div className={styles.descriptionContainer}>
                                                <p className={styles.desc}>{_t.desc}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <Button
                                    variant="secondary"
                                    onClick={
                                        ((e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            handleChangeSelectedTemplate(_t.template);
                                        }) as unknown as () => void
                                    }
                                >
                                    {bundleConfig[fieldKeys.template] === _t.template ? "Selected" : "Select"}
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    );
};

export default TemplateSelectionScreen;
